/**
 * Card - Core Component (Enhanced Wrapper)
 *
 * This component wraps shadcn/ui Card with significant project-specific enhancements.
 * It adds variants, sizes, color schemes, and interactive states.
 *
 * Enhancements over shadcn/ui:
 * - Visual variants: default, elevated, outlined, ghost
 * - Size variants: sm, md, lg (affects padding and gap)
 * - Color schemes: default, muted, accent
 * - Clickable state with hover effects
 * - Selected state with ring indicator
 * - Disabled state with reduced opacity
 * - Design token: rounded-card-radius for consistent border-radius
 *
 * Sub-components:
 * - CardHeader, CardTitle, CardDescription, CardAction, CardContent, CardFooter
 * - CardTitle has size variants (sm, default, lg)
 * - CardFooter has direction prop (row, column)
 *
 * @see {@link file://./../ui/card.tsx} for the underlying implementation
 */
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  type ComponentProps,
  type ReactNode,
  type ElementType,
} from 'react';
import {
  Card as ShadcnCard,
  CardHeader as ShadcnCardHeader,
  CardTitle as ShadcnCardTitle,
  CardDescription as ShadcnCardDescription,
  CardAction as ShadcnCardAction,
  CardContent as ShadcnCardContent,
  CardFooter as ShadcnCardFooter,
} from '@/app/_components/ui/card';
import { cn } from '@/app/_lib/utils';

const cardVariants = cva('transition-[border-color,box-shadow,transform] duration-200', {
  variants: {
    variant: {
      default: '',
      elevated: 'shadow-card xl:shadow-card-desktop border-transparent',
      outlined: 'border-2 shadow-none',
      ghost: 'border-transparent shadow-none bg-transparent',
    },
    size: {
      sm: 'py-4 gap-4',
      md: 'py-6 gap-6',
      lg: 'py-8 gap-8',
    },
    colorScheme: {
      default: '',
      muted: 'bg-muted/50 text-muted-foreground',
      accent: 'bg-accent text-accent-foreground',
    },
    clickable: {
      true: 'cursor-pointer hover:border-primary/50 active:scale-[0.98]',
      false: '',
    },
    disabled: {
      true: 'opacity-50 pointer-events-none',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'elevated',
      clickable: true,
      className: 'hover:shadow-lg',
    },
  ],
  defaultVariants: {
    variant: 'default',
    size: 'md',
    colorScheme: 'default',
    clickable: false,
    disabled: false,
  },
});

export interface CardProps
  extends ComponentProps<'div'>, VariantProps<typeof cardVariants> {
  /**
   * Render card as a different HTML element (e.g., 'article', 'section')
   * @default 'div'
   */
  as?: ElementType;
  /**
   * Render as child element (uses Slot from Radix)
   * Useful for wrapping cards with Link components
   * @default false
   */
  asChild?: boolean;
  /**
   * Card visual style variant
   * @default 'default'
   */
  variant?: 'default' | 'elevated' | 'outlined' | 'ghost';
  /**
   * Card size with padding adjustments
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Color scheme for the card
   * @default 'default'
   */
  colorScheme?: 'default' | 'muted' | 'accent';
  /**
   * Inner layout mode. 'flex' is the default vertical stack; 'grid' turns the
   * card into a grid container (e.g. for `grid-rows-subgrid` consumers) without
   * leaving a dead `flex-col` class behind.
   * @default 'flex'
   */
  layout?: 'flex' | 'grid';
  /**
   * Makes the card clickable with hover effects
   * @default false
   */
  clickable?: boolean;
  /**
   * Whether the card is selected (adds visual indicator)
   * @default false
   */
  selected?: boolean;
  /**
   * Disables the card and reduces opacity
   * @default false
   */
  disabled?: boolean;
  /**
   * Card content
   */
  children?: ReactNode;
}

export interface CardHeaderProps extends ComponentProps<'div'> {
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Header content
   */
  children?: ReactNode;
}

export interface CardTitleProps extends ComponentProps<'div'> {
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Title content
   */
  children?: ReactNode;
  /**
   * Title size variant
   * @default 'default'
   */
  size?: 'sm' | 'default' | 'lg';
  /**
   * Render as child element (uses Slot from Radix)
   * @default false
   */
  asChild?: boolean;
}

export interface CardDescriptionProps extends ComponentProps<'div'> {
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Description content
   */
  children?: ReactNode;
}

export interface CardActionProps extends ComponentProps<'div'> {
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Action content (usually buttons)
   */
  children?: ReactNode;
}

export interface CardContentProps extends ComponentProps<'div'> {
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Content
   */
  children?: ReactNode;
}

export interface CardFooterProps extends ComponentProps<'div'> {
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Footer content (usually actions/buttons)
   */
  children?: ReactNode;
  /**
   * Flex direction for footer items
   * @default 'row'
   */
  direction?: 'row' | 'column';
}

const titleVariants = cva('', {
  variants: {
    size: {
      sm: 'text-sm',
      default: 'text-base',
      lg: 'text-lg',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      as,
      asChild = false,
      variant,
      size,
      colorScheme,
      clickable,
      selected,
      disabled,
      layout = 'flex',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const layoutClass = layout === 'grid' ? 'grid' : 'flex flex-col';
    const cardClassName = cn(
      cardVariants({ variant, size, colorScheme, clickable, disabled }),
      selected && 'ring-2 ring-primary ring-offset-2',
      'min-w-card rounded-card-radius border-card',
      className
    );

    // Render as Slot for composition with Link/button components
    if (asChild) {
      return (
        <Slot
          ref={ref}
          data-slot="card"
          className={cn(
            layoutClass,
            'gap-6 rounded-xl border bg-card py-6 text-card-foreground shadow-sm',
            cardClassName
          )}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    // Render as custom element if `as` prop is provided
    if (as) {
      const Component = as;
      return (
        <Component
          ref={ref}
          data-slot="card"
          className={cn(
            layoutClass,
            'gap-6 rounded-xl border bg-card py-6 text-card-foreground',
            cardClassName
          )}
          {...props}
        >
          {children}
        </Component>
      );
    }

    return (
      <ShadcnCard ref={ref} className={cardClassName} {...props}>
        {children}
      </ShadcnCard>
    );
  }
);

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardHeader ref={ref} className={cn(className)} {...props}>
        {children}
      </ShadcnCardHeader>
    );
  }
);

export const CardTitle = forwardRef<
  HTMLDivElement,
  CardTitleProps & VariantProps<typeof titleVariants>
>(
  (
    { className, size = 'default', asChild = false, children, ...props },
    ref
  ) => {
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={cn(titleVariants({ size }), className)}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <ShadcnCardTitle
        ref={ref}
        className={cn(titleVariants({ size }), className)}
        {...props}
      >
        {children}
      </ShadcnCardTitle>
    );
  }
);

export const CardDescription = forwardRef<HTMLDivElement, CardDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardDescription ref={ref} className={cn(className)} {...props}>
        {children}
      </ShadcnCardDescription>
    );
  }
);

export const CardAction = forwardRef<HTMLDivElement, CardActionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardAction ref={ref} className={cn(className)} {...props}>
        {children}
      </ShadcnCardAction>
    );
  }
);

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardContent ref={ref} className={cn(className)} {...props}>
        {children}
      </ShadcnCardContent>
    );
  }
);

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, direction = 'row', children, ...props }, ref) => {
    return (
      <ShadcnCardFooter
        ref={ref}
        className={cn(
          direction === 'column' && 'flex-col',
          direction === 'row' && 'flex-row',
          className
        )}
        {...props}
      >
        {children}
      </ShadcnCardFooter>
    );
  }
);

Card.displayName = 'Card';
CardHeader.displayName = 'CardHeader';
CardTitle.displayName = 'CardTitle';
CardDescription.displayName = 'CardDescription';
CardAction.displayName = 'CardAction';
CardContent.displayName = 'CardContent';
CardFooter.displayName = 'CardFooter';

export default Card;
